import React, { CSSProperties, FocusEvent, useRef, useState } from 'react';
import { Controller, Control } from 'react-hook-form';
import { Button, Divider, Input, Select, Space, theme } from 'antd';
import type { InputRef, SelectProps } from 'antd';
import { ErrorText } from '../Elements';
import FormLabel from './FormLabel';
import { PlusOutlined } from '@ant-design/icons';

const { useToken } = theme;

type DefaultOptionType = {
  value: string;
  label: string;
};
const defaultFieldNames: DefaultOptionType = {
  value: 'value',
  label: 'label',
};
interface SelectFieldProps {
  label?: string | React.ReactNode;
  placeholder?: string;
  control: any;
  name: string;
  options: Record<string, any>[];
  error?: string;
  showSearch?: boolean;
  size?: 'large' | 'middle' | 'small';
  formDirection?: 'row' | 'column';
  disabled?: boolean;
  required?: boolean;
  fieldNames?: any;
  onBlur?: SelectProps['onBlur'];
  onChange?: SelectProps['onChange'];
  filterName?: string;
  rootStyles?: CSSProperties;
  mode?: 'multiple' | 'tags';
  allowClear?: boolean;
  loading?: boolean;
  allowAddItem?: boolean;
  addItemPlaceholder?: string;
  onAddItem?: (value: string) => void;
}

const SelectField: React.FC<SelectFieldProps> = ({
  label,
  control,
  name,
  placeholder,
  options,
  showSearch = false,
  error,
  size = 'middle',
  formDirection = 'column',
  disabled = false,
  required = false,
  onBlur,
  onChange,
  fieldNames = { defaultFieldNames },
  filterName = 'country',
  rootStyles,
  mode,
  allowClear = false,
  loading = false,
  allowAddItem = false,
  addItemPlaceholder = 'Enter new item',
  onAddItem,
}) => {
  const errorStatus = error ? 'error' : undefined;

  const [newItemName, setNewItemName] = useState('');
  const inputRef = useRef<InputRef>(null);

  const handleAddItem = (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    e.preventDefault();
    if (!newItemName.trim()) return;
    onAddItem?.(newItemName.trim());
    setNewItemName('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };
  return (
    <div className={`form-control ${formDirection}`} style={rootStyles}>
      {label && <FormLabel required={required} label={label as string} />}
      <div className="input-containter">
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              disabled={disabled}
              mode={mode}
              allowClear={allowClear}
              showSearch={showSearch}
              onChange={(value, option) => {
                field.onChange(value);
                onChange?.(value, option);
              }}
              status={errorStatus}
              options={options}
              placeholder={placeholder}
              size={size}
              style={{ width: '100%', display: 'block' }}
              fieldNames={fieldNames}
              onBlur={(e) => {
                field.onBlur();
                onBlur?.(e);
              }}
              loading={loading}
              filterOption={(input, option) =>
                name.includes(filterName)
                  ? option?.countryName
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  : option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              {...(allowAddItem && {
                popupRender: (menu) => (
                  <>
                    {menu}
                    <Divider style={{ margin: '8px 0' }} />
                    <Space style={{ padding: '0 8px 4px' }}>
                      <Input
                        placeholder={addItemPlaceholder}
                        ref={inputRef}
                        value={newItemName}
                        onChange={(e) => setNewItemName(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                      />
                      <Button
                        type="text"
                        icon={<PlusOutlined />}
                        onClick={handleAddItem}
                      >
                        Add
                      </Button>
                    </Space>
                  </>
                ),
              })}
            />
          )}
        />
        {error && <ErrorText error={error} />}
      </div>
    </div>
  );
};

export default SelectField;
